import React, { useEffect, useState } from "react";
import { useTheme } from "@mui/material/styles";
import {
  AppBar,
  Card,
  Paper,
  Snackbar,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
  Tooltip,
  TextField,
  Box,
} from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import CloseIcon from "@mui/icons-material/Close";
import {
  LastPage,
  FirstPage,
  NavigateBefore,
  NavigateNext,
} from "@mui/icons-material";
import Typography from "@mui/material/Typography";
import MDBox from "components/MDBox";
import MDButton from "components/MDButton";
import MDInput from "components/MDInput";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import { GET } from "APIclient";
import * as XLSX from "xlsx";
import { formatDesignation } from "constants/ApplicationConstants";
import properties from "constants/ApplicationConstants";

const columns = [
  { field: "id", headerName: "#", width: 30 },
  { field: "empId", headerName: "EMP ID", width: 80 },
  { field: "employeeName", headerName: "Employee Name", width: 200 },
  { field: "designation", headerName: "Designation", width: 220 },
  {
    field: "currentCompanyExperience",
    headerName: "BNT Exp (Yrs.)",
    width: 150,
  },
  {
    field: "previousCompanyExperience",
    headerName: "Other Exp (Yrs.)",
    width: 150,
  },
  { field: "totalExperience", headerName: "Total Exp (Yrs.)", width: 150 },
];

function CustomPagination({
  page,
  pageSize,
  totalRows,
  totalPages,
  onPageChange,
  onGoToLastPage,
  loading,
}) {
  const handleFirstPage = () => onPageChange(0);
  const handlePreviousPage = () => onPageChange(Math.max(0, page - 1));
  const handleNextPage = () => onPageChange(Math.min(totalPages - 1, page + 1));
  const handleLastPage = () => onGoToLastPage();

  const startRow = page * pageSize + 1;
  const endRow = Math.min((page + 1) * pageSize, totalRows);

  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        p: 1,
        borderTop: "1px solid #e0e0e0",
      }}
    >
      <Typography variant="caption" sx={{ color: "#666" }}>
        {totalRows > 0 ? `${startRow}-${endRow} of ${totalRows}` : "No rows"}
      </Typography>

      <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
        <Typography variant="caption" sx={{ mr: 2, color: "#666" }}>
          Page {page + 1} of {totalPages || 1}
        </Typography>

        <Tooltip title="First Page">
          <IconButton
            onClick={handleFirstPage}
            disabled={page === 0 || loading}
            size="small"
          >
            <FirstPage />
          </IconButton>
        </Tooltip>

        <Tooltip title="Previous Page">
          <IconButton
            onClick={handlePreviousPage}
            disabled={page === 0 || loading}
            size="small"
          >
            <NavigateBefore />
          </IconButton>
        </Tooltip>

        <Tooltip title="Next Page">
          <IconButton
            onClick={handleNextPage}
            disabled={page >= totalPages - 1 || loading}
            size="small"
          >
            <NavigateNext />
          </IconButton>
        </Tooltip>

        <Tooltip title="Last Page">
          <IconButton
            onClick={handleLastPage}
            disabled={page >= totalPages - 1 || loading}
            size="small"
            sx={{
              backgroundColor: properties?.colors?.bgcolorbluethem || "#1976d2",
              color: "white",
              "&:hover": {
                backgroundColor:
                  properties?.colors?.bgcolorbluethem || "#1565c0",
              },
              "&.Mui-disabled": {
                backgroundColor: "#e0e0e0",
                color: "#9e9e9e",
              },
            }}
          >
            <LastPage />
          </IconButton>
        </Tooltip>
      </Box>
    </Box>
  );
}


const ExperienceReport = () => {
  const theme = useTheme();
  const [experienceReports, setExperienceReports] = useState([]);
  const [filteredExperienceReports, setFilteredExperienceReports] = useState([]);

  const [paginationModel, setPaginationModel] = useState({
    page: 0,
    pageSize: 10,
  });
  const [totalRows, setTotalRows] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [loading, setLoading] = useState(false);

  const [searchEmployeeId, setSearchEmployeeId] = useState("");
  const [searchEmployeeName, setSearchEmployeeName] = useState("");

  const [openModal, setOpenModal] = useState(false);
  const [selectedReport, setSelectedReport] = useState(null);

  const [snackOpen, setSnackOpen] = useState(false);
  const [snackMessage, setSnackMessage] = useState("");
  const [snackColor, setSnackColor] = useState("success");


  const fetchExperienceReports = async (page, pageSize, empId, employeeName) => {
    try {
      setLoading(true);
      const response = await GET(
        `manager/experience-report?page=${page}&size=${pageSize}&empId=${empId}&empName=${employeeName}`
      );

      if (
        response.status === 200 &&
        Array.isArray(response.data?.data?.paginatedData)
      ) {
        const data = response.data.data.paginatedData.map((item, index) => ({
          id: page * pageSize + index + 1,
          empId: item.empId,
          employeeName: item.employeeName,
          designation: formatDesignation(item.designation),
          currentCompanyExperience: item.currentCompanyExperience,
          previousCompanyExperience: item.previousCompanyExperience,
          totalExperience: item.totalExperience,
          handleView: handleViewDetails,
        }));

        setExperienceReports(data);
        setFilteredExperienceReports(data);
        setTotalRows(response.data.data.totalItems);
        setTotalPages(response.data.data.totalPages);
      } else {
        throw new Error("Invalid response format");
      }
    } catch (error) {
      console.error("Error fetching reports:", error);
      setSnackMessage("Failed to fetch experience reports.");
      setSnackColor("error");
      setSnackOpen(true);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchExperienceReports(
      paginationModel.page,
      paginationModel.pageSize,
      searchEmployeeId,
      searchEmployeeName
    );
  }, [paginationModel]);


  const handleSearch = () => {
    setPaginationModel({ ...paginationModel, page: 0 });
    fetchExperienceReports(0, paginationModel.pageSize, searchEmployeeId, searchEmployeeName);
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter") handleSearch();
  };

  const handlePageChange = (newPage) => {
    setPaginationModel({ ...paginationModel, page: newPage });
    setTimeout(() => window.scrollTo(0, 0), 0);
  };

  const handleGoToLastPage = () => {
    if (totalPages > 0) {
      const lastPageIndex = totalPages - 1;
      setPaginationModel({ ...paginationModel, page: lastPageIndex });
      setTimeout(() => window.scrollTo(0, 0), 0);
    }
  };

  const handleViewDetails = (report) => {
    setSelectedReport(report);
    setOpenModal(true);
  };

  const handleCloseModal = () => {
    setOpenModal(false);
    setSelectedReport(null);
  };

  const handleDownloadExcel = () => {
    const worksheet = XLSX.utils.json_to_sheet(filteredExperienceReports);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Experience Report");
    XLSX.writeFile(workbook, "Experience_report.xlsx");
  };


  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox>
        <AppBar position="static">
          <Card>
            <MDBox pt={2} px={6} pb={2} >
              <MDBox sx={{ display: "grid", gridTemplateColumns: "repeat(12, 1fr)", gap: 3, alignItems: "center" }}>
                <Typography
                  sx={{
                    gridColumn: "span 3",
                    color: "textPrimary",
                    fontFamily: properties.fontFamily,
                    fontSize: "15px",
                    fontWeight: "bold"
                  }}
                >
                  Experience Report
                </Typography>
                <MDInput
                  sx={{ gridColumn: "span 2" }}
                  value={searchEmployeeId}
                  onChange={(e) => setSearchEmployeeId(e.target.value)}
                  onKeyDown={handleKeyDown}
                  label="Employee ID"
                />
                <MDInput
                  sx={{ gridColumn: "span 3" }}
                  value={searchEmployeeName}
                  onChange={(e) => setSearchEmployeeName(e.target.value)}
                  onKeyDown={handleKeyDown}
                  label="Employee Name"
                />
                <MDButton
                  sx={{ gridColumn: "span 2", background: properties.colors.bgcolorbluethem }}
                  variant="gradient"
                  color="info"
                  onClick={handleSearch}
                >
                  Search
                </MDButton>
                <MDButton
                  sx={{ gridColumn: "span 2" }}
                  variant="gradient"
                  color="success"
                  onClick={handleDownloadExcel}
                >
                  Download
                </MDButton>
              </MDBox>
            </MDBox>
          </Card>
        </AppBar>
     
            <MDBox>
              <Paper>
                <DataGrid
                  rows={filteredExperienceReports}
                  columns={columns}
                  paginationMode="server"
                  rowCount={totalRows}
                  pageSizeOptions={[10]}
                  paginationModel={paginationModel}
                  loading={loading}
                  hideFooter
                  getRowId={(row) => row.id}
                  sx={{
                    height: "450px",
                    fontFamily: properties.fontFamily,
                    fontSize: "14px",
                    "& .MuiDataGrid-scrollbar": {
                      width: "10px !important",
                    },
                  }}
                />

         
                <CustomPagination
                  page={paginationModel.page}
                  pageSize={paginationModel.pageSize}
                  totalRows={totalRows}
                  totalPages={totalPages}
                  onPageChange={handlePageChange}
                  onGoToLastPage={handleGoToLastPage}
                  loading={loading}
                />
              </Paper>
            </MDBox>

     
        <Snackbar
          open={snackOpen}
          autoHideDuration={3000}
          onClose={() => setSnackOpen(false)}
          anchorOrigin={{ vertical: "top", horizontal: "right" }}
        >
          <Alert onClose={() => setSnackOpen(false)} severity={snackColor}>
            {snackMessage}
          </Alert>
        </Snackbar>

   
        <Dialog open={openModal} onClose={handleCloseModal} fullWidth maxWidth="sm">
          <DialogTitle>
            Experience Report Details
            <IconButton edge="end" onClick={handleCloseModal}>
              <CloseIcon />
            </IconButton>
          </DialogTitle>
          <DialogContent>
            {selectedReport && (
              <form>
                <TextField
                  label="Employee ID"
                  value={selectedReport.empId || "N/A"}
                  fullWidth
                  InputProps={{ readOnly: true }}
                  margin="normal"
                />
                <TextField
                  label="Employee Name"
                  value={selectedReport.employeeName || "N/A"}
                  fullWidth
                  InputProps={{ readOnly: true }}
                  margin="normal"
                />
                <TextField
                  label="Designation"
                  value={selectedReport.designation || "N/A"}
                  fullWidth
                  InputProps={{ readOnly: true }}
                  margin="normal"
                />
                <TextField
                  label="Current Company Experience"
                  value={selectedReport.currentCompanyExperience || "N/A"}
                  fullWidth
                  InputProps={{ readOnly: true }}
                  margin="normal"
                />
                <TextField
                  label="Previous Company Experience"
                  value={selectedReport.previousCompanyExperience || "N/A"}
                  fullWidth
                  InputProps={{ readOnly: true }}
                  margin="normal"
                />
                <TextField
                  label="Total Experience"
                  value={selectedReport.totalExperience || "N/A"}
                  fullWidth
                  InputProps={{ readOnly: true }}
                  margin="normal"
                />
              </form>
            )}
          </DialogContent>
        </Dialog>
      </MDBox>
    </DashboardLayout>
  );
};

export default ExperienceReport;
